// src/services/staffConfirmation/staffConfirmationState.ts - whether "Confirm with
// Google" is on for this session, readable from anywhere (the axios interceptors,
// the prompt broker, React). No imports beyond React, so the transport can read
// it without pulling in the launch-control screens. The gate is read and written
// by staffConfirmationControls.
import { useSyncExternalStore } from "react";

interface ControlsState {
  gateOn: boolean;
  identityPolicyOff: boolean;
  /** The api asked for a confirmation this session, whatever the gate read said. */
  demanded: boolean;
}

let state: ControlsState = {
  gateOn: false,
  identityPolicyOff: false,
  demanded: false,
};
let generation = 0;
const listeners = new Set<() => void>();

const publish = (next: ControlsState) => {
  state = next;
  listeners.forEach((listener) => listener());
};

/**
 * The staff tenant's issuer, e.g. `https://id.80eight.io/t/staff`, or null when
 * none is configured or it is not https (plain http only for a local 88ID).
 */
export const identityStaffIssuer = (
  configured: string | undefined = process.env.REACT_APP_IDENTITY_ISSUER
): string | null => {
  const raw = configured?.trim();
  if (!raw) return null;
  try {
    const url = new URL(raw);
    const local = url.hostname === "localhost" || url.hostname === "127.0.0.1";
    if (url.protocol !== "https:" && !(local && url.protocol === "http:"))
      return null;
    return url.href.replace(/\/+$/, "");
  } catch {
    return null;
  }
};

/** True only when both controls allow it and 88ID is configured. */
export const isStaffConfirmationOn = (): boolean =>
  (state.gateOn || state.demanded) &&
  !state.identityPolicyOff &&
  identityStaffIssuer() !== null;

/** Start a read; the returned check says whether it is still the latest. */
export const beginGateRead = (): (() => boolean) => {
  const mine = ++generation;
  return () => mine === generation;
};

// Keeps the 88ID latch: a re-read of the gate (the sign-in re-runs on every token
// renewal) must not undo "88ID said its policy is off". Only sign-out clears it.
export const setGateOn = (gateOn: boolean): void =>
  publish({ ...state, gateOn });

/** 88ID answered 404: its tenant policy is off. OFF for the rest of the session. */
export const noteIdentityConfirmationUnavailable = (): void => {
  if (!state.identityPolicyOff) publish({ ...state, identityPolicyOff: true });
};

/**
 * The api refused a request for want of a confirmation, so it is armed whatever
 * the gate read said: the gate is read once at sign-in, and a failed read means
 * OFF. Holds until sign-out, so a later re-read cannot undo what the api just said.
 */
export const noteConfirmationDemanded = (): void => {
  if (!state.demanded) publish({ ...state, demanded: true });
};

/** Sign-out: forget everything, and let no read still in flight land. */
export const resetStaffConfirmationControls = (): void => {
  generation += 1;
  publish({ gateOn: false, identityPolicyOff: false, demanded: false });
};

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

/** React read of isStaffConfirmationOn, re-rendered when either control moves. */
export const useStaffConfirmationOn = (): boolean =>
  useSyncExternalStore(subscribe, isStaffConfirmationOn, isStaffConfirmationOn);

/** Test seam: set the gate directly. */
export const setStaffConfirmationGateForTests = (gateOn: boolean): void => {
  generation += 1;
  publish({ gateOn, identityPolicyOff: false, demanded: false });
};

// ---- SESSION_ENDED ----------------------------------------------------------
// 88ID answers SESSION_ENDED when the staff session behind a confirmation is
// over (8 h absolute, 30 min idle). No confirmation can follow, so the operator
// goes back to sign-in: the AuthProvider listens here and signs out.
const sessionEndedListeners = new Set<() => void>();

export const onStaffSessionEnded = (listener: () => void): (() => void) => {
  sessionEndedListeners.add(listener);
  return () => {
    sessionEndedListeners.delete(listener);
  };
};

export const noteStaffSessionEnded = (): void => {
  sessionEndedListeners.forEach((listener) => listener());
};
